import * as d3 from 'd3'
import { ScaleLinear } from 'd3'

import { VideoDimensionsIProps } from '@/features/video-player'
import {
  BoxInput,
  Box as BoxNode,
  TailgateGrid,
} from '@/graphql/generated/schemas'

import { BoxType, CornerPoint, TailgateBox } from '../types/types'
import { IsSimple } from './polyValidation'

export const EMPTY_BOX: TailgateBox = { corners: [], isValid: false }
export const IMAGE_WIDTH = 650
export const IMAGE_HEIGHT = 400
export const N_CORNERS = 4
export const CORNER_RADIUS = 5

const findPreviousIndex = (i: number) => (i == 0 ? N_CORNERS - 1 : i - 1)

const findNextIndex = (i: number) => (i == N_CORNERS - 1 ? 0 : i + 1)

/**
 * This recursive functions finds n-th next index in a sequence of array indices.
 *
 * It assumes that array has lenght 4.
 * Array of four corners have indices [0, 1, 2, 3].
 *
 * To find incremented indices starting from 1, we must call:
 *   findNthIndex(1, 1, 'increment') <- should return 2
 *   findNthIndex(1, 2, 'increment') <- should return 3
 *   findNthIndex(1, 3, 'increment') <- should return 0 (because 3 is the last index)
 *   findNthIndex(1, 4, 'increment') <- should return 1
 *
 * To find decremented indices starting from 1, we must call:
 *   findNthIndex(1, 1, 'decrement') <- should return 0
 *   findNthIndex(1, 2, 'decrement') <- should return 3 (because 0 is the first index)
 *   findNthIndex(1, 3, 'decrement') <- should return 2
 *   findNthIndex(1, 4, 'decrement') <- should return 1
 */
export const findNthIndex = (
  i: number,
  steps: number,
  direction: 'increment' | 'decrement'
) => {
  const indexUpdateFunction =
    direction === 'increment' ? findNextIndex : findPreviousIndex
  if (steps === 0) {
    return i
  }
  if (steps === 1) {
    return indexUpdateFunction(i)
  }
  return findNthIndex(indexUpdateFunction(i), steps - 1, direction)
}

/**
 * This function implements an algorithm to assign position labels to tailgate box corners.
 *
 * It assumes that corners array in the TailgateBox object fulfills three conditions:
 * 1. There are exactly four corners.
 * 2. For any corner, next corner in the array is connected with that corner with an edge.
 * 3. Last corner in the array is connected with the first corner in the array.
 *
 * The algorithm works as follows:
 *
 * 1. First, index of the top left corner is found using min(x + y) formula.
 *    See <https://stackoverflow.com/a/13507770> for a reference.
 * 2. Then, index of the top right corner is found by determining which corner connected with the
 *    top left corner is closer to the top right corner of the image. Since connected corners have
 *    adjacent places in the array, top right corner could have incremented or decremented index
 *    relative to the index of the top left corner.
 * 3. Having established index of the top left corner and top right corner, next two indeces are
 *    found by extending the sequence created by the first two indices.
 */
export const getBoxInput = (box: TailgateBox): BoxInput => {
  const xList = box?.corners.map((corner) => corner.x)
  const yList = box?.corners.map((corner) => corner.y)
  const sumList = xList.map((x, i) => x + yList[i])
  const difList = xList.map((x, i) => x - yList[i])

  // Step 1. Find index of the top left corner
  const topLeftIndex = sumList.indexOf(Math.min(...sumList))

  // Step 2. Determine if the top right corner has incremented or decremented index
  const topRightIndexDirection =
    difList[findNextIndex(topLeftIndex)] >
    difList[findPreviousIndex(topLeftIndex)]
      ? 'increment'
      : 'decrement'

  // Step 3. Find all indices
  const indices = [...Array(4).keys()].map((increment) =>
    findNthIndex(topLeftIndex, increment, topRightIndexDirection)
  )

  return {
    topLeft: {
      x: box?.corners?.[indices[0]]?.scaledX,
      y: box?.corners?.[indices[0]]?.scaledY,
    },
    topRight: {
      x: box?.corners?.[indices[1]]?.scaledX,
      y: box?.corners?.[indices[1]]?.scaledY,
    },
    bottomRight: {
      x: box?.corners?.[indices[2]]?.scaledX,
      y: box?.corners?.[indices[2]]?.scaledY,
    },
    bottomLeft: {
      x: box?.corners?.[indices[3]]?.scaledX,
      y: box?.corners?.[indices[3]]?.scaledY,
    },
  }
}

export const getBoxInputDefaultOrder = (box: TailgateBox): BoxInput => ({
  topLeft: box.corners[0],
  topRight: box.corners[1],
  bottomRight: box.corners[2],
  bottomLeft: box.corners[3],
})

export const updateTailgateBox = (
  tailgateBox: TailgateBox,
  draggedPoint: CornerPoint,
  scaleX: ScaleLinear<number, number, never>,
  scaleY: ScaleLinear<number, number, never>
): TailgateBox => {
  const corners = tailgateBox.corners.map((point) => {
    if (point.id === draggedPoint.id) {
      return {
        ...draggedPoint,
        scaledX: scaleX(draggedPoint.x),
        scaledY: scaleY(draggedPoint.y),
      }
    }
    return point
  })

  const isValid = isValidBox(tailgateBox)
  return { corners, isValid }
}

export const isValidBox = (tailgateBox: TailgateBox): boolean => {
  const { corners } = tailgateBox
  return IsSimple([
    corners[0]?.x,
    corners[0]?.y,
    corners[1]?.x,
    corners[1]?.y,
    corners[2]?.x,
    corners[2]?.y,
    corners[3]?.x,
    corners[3]?.y,
  ])
}

export const getClosestPointWithinImageBounds = (
  x: number,
  y: number,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number } => {
  // If the coordinate value is negative return 0 (left or top image bound)
  // If the coordinate exceeds its max value (imageWidth for x, imageHeight for y) return that max value (bottom or right image bound)
  // Otherwise return original value
  return {
    x: x < 0 ? 0 : x > imageWidth ? imageWidth : x,
    y: y < 0 ? 0 : y > imageHeight ? imageHeight : y,
  }
}

export const getTailgateBox = (
  boxNode: BoxNode,
  boxType: BoxType,
  scaleX: ScaleLinear<number, number, never>,
  scaleY: ScaleLinear<number, number, never>
): TailgateBox => {
  if (boxNode === undefined || boxNode === null) {
    return null
  }

  const { x: x1, y: y1 } = boxNode.topLeft
  const { x: x2, y: y2 } = boxNode.topRight
  const { x: x3, y: y3 } = boxNode.bottomRight
  const { x: x4, y: y4 } = boxNode.bottomLeft

  return {
    isValid: true,
    corners: [
      {
        scaledX: x1,
        scaledY: y1,
        x: scaleX(x1),
        y: scaleY(y1),
        id: 1,
        type: boxType,
      },
      {
        scaledX: x2,
        scaledY: y2,
        x: scaleX(x2),
        y: scaleY(y2),
        id: 2,
        type: boxType,
      },
      {
        scaledX: x3,
        scaledY: y3,
        x: scaleX(x3),
        y: scaleY(y3),
        id: 3,
        type: boxType,
      },
      {
        scaledX: x4,
        scaledY: y4,
        x: scaleX(x4),
        y: scaleY(y4),
        id: 4,
        type: boxType,
      },
    ],
  }
}

export const getInitialTailgateGrid = (
  videoDimensions: VideoDimensionsIProps,
  tailgateGrid: TailgateGrid,
  frameDimensions?: VideoDimensionsIProps
) => {
  const initialScaleX = getScale(videoDimensions?.width, frameDimensions?.width)
  const initialScaleY = getScale(
    videoDimensions?.height,
    frameDimensions?.height
  )
  const initialInBox = getTailgateBox(
    tailgateGrid?.inBox,
    BoxType.IN_BOX,
    initialScaleX,
    initialScaleY
  )
  const initialOutBox = getTailgateBox(
    tailgateGrid?.outBox,
    BoxType.OUT_BOX,
    initialScaleX,
    initialScaleY
  )
  return { initialInBox, initialOutBox }
}

export const isBoxSetup = (box: TailgateBox) => {
  if (box?.corners?.length > 3) {
    return true
  }
  return false
}

export const getScale = (toDomain: number, toRange: number) => {
  const scale = d3.scaleLinear().domain([0, toDomain]).range([0, toRange])
  return scale
}

export const getGridProperties = (inBox: TailgateBox, outBox: TailgateBox) => ({
  isGridValid: inBox?.isValid && outBox?.isValid,
  isGridEmpty: inBox?.corners.length === 0 && outBox?.corners.length === 0,
})

export const areBoxesEqual = (boxA: TailgateBox, boxB: TailgateBox) => {
  return [...Array(4).keys()]
    .map(
      (i) =>
        boxA?.corners[i]?.x === boxB?.corners[i]?.x &&
        boxA?.corners[i]?.y === boxB?.corners[i]?.y
    )
    .every(Boolean)
}

export const copyBox = (box: TailgateBox): TailgateBox => ({
  ...box,
  corners: [
    { ...box.corners[0] },
    { ...box.corners[1] },
    { ...box.corners[2] },
    { ...box.corners[3] },
  ],
})

/**
 * Takes max sizes divided by src sizes, takes the smaller of the two,
 * and scales the source dimensions by the derived ratio.
 *
 * @param {number | undefined} srcWidth width of source image
 * @param {number | undefined} srcHeight height of source image
 * @param {number} maxWidth maximum available width
 * @param {number} maxHeight maximum available height
 */
export const calculateAspectRatioFit = (
  srcWidth: number | undefined,
  srcHeight: number | undefined,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  if (srcWidth === undefined || srcHeight === undefined) {
    // Hopefully this doesn't happen often, but if we're not given source dimensions,
    // let's just default to fill the allotted space
    return {
      width: maxWidth,
      height: maxHeight,
    }
  }

  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight)

  return {
    width: srcWidth * ratio,
    height: srcHeight * ratio,
  }
}
